export const assetKind = (asset) => ({ CAM: "Câmera", SER: "Servidor", SWI: "Switch", NOB: "Nobreak", RAC: "Rack" })[asset.systemType] || asset.assetType || "Equipamento";
export const assetLabel = (asset) => asset.pointName || asset.name;
export const assetLocation = (asset) => asset.building || asset.area || asset.location || "Local não informado";
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/^unidade\s+/i, "").trim().toLocaleLowerCase("pt-BR");
export const topologyLocation = (asset, unitName) => {
  const place = assetLocation(asset);
  if (normalize(place) !== normalize(unitName)) return place;
  return [asset.area, asset.location].find((value) => value && normalize(value) !== normalize(unitName)) || "Local não informado";
};
const rank = (asset) => ({ SWI: 0, NOB: 1, RAC: 2, SER: 3, CAM: 4 })[asset.systemType] ?? 5;
const sortAssets = (a, b) => rank(a) - rank(b) || assetLabel(a).localeCompare(assetLabel(b), "pt-BR");
export const topologyAssetPage = (assets, asset, unitName, pageSize = 20) => {
  const location = topologyLocation(asset, unitName);
  const members = assets.filter((item) => topologyLocation(item, unitName) === location).sort(sortAssets);
  return Math.max(0, Math.floor(members.findIndex((item) => String(item.id) === String(asset.id)) / pageSize));
};

// Progressive view: the overview displays places; a place displays one page
// of equipment. This keeps labels legible in units with hundreds of cameras.
export function buildTopology(assets, unitName, { location = null, page = 0, pageSize = 20 } = {}) {
  const byId = new Map(assets.map((asset) => [Number(asset.id), asset]));
  const grouped = new Map();
  assets.forEach((asset) => {
    const place = topologyLocation(asset, unitName);
    if (!grouped.has(place)) grouped.set(place, []);
    grouped.get(place).push(asset);
  });
  const locations = [...grouped].sort(([a], [b]) => a.localeCompare(b, "pt-BR")).map(([name, members], index) => ({ id: `location:${index}`, name, count: members.length }));
  const linked = (asset) => [asset.switchAssetId, asset.upsAssetId].some((id) => byId.has(Number(id)) && Number(id) !== Number(asset.id)) || assets.some((other) => Number(other.switchAssetId) === Number(asset.id) || Number(other.upsAssetId) === Number(asset.id));
  const unlinked = assets.filter((asset) => !linked(asset));
  const physicalCount = assets.reduce((total, asset) => total + [asset.switchAssetId, asset.upsAssetId].filter((id) => byId.has(Number(id)) && Number(id) !== Number(asset.id)).length, 0);
  const unit = { id: "unit", type: "unit", label: unitName || "Unidade", x: 100, y: 100 };
  if (location === null) {
    const columns = Math.min(4, Math.max(1, Math.ceil(Math.sqrt(locations.length))));
    const nodes = [unit, ...locations.map((item, index) => ({ id: item.id, type: "location", label: item.name, count: item.count, x: 330 + index % columns * 220, y: 85 + Math.floor(index / columns) * 145 }))];
    return { nodes, edges: locations.map((item) => ({ source: "unit", target: item.id, type: "group" })), locations, unlinked, physicalCount, page: 0, totalPages: 1, totalAssets: assets.length };
  }
  const selected = locations.find((item) => item.name === location);
  if (!selected) return { nodes: [unit], edges: [], locations, unlinked, physicalCount, page: 0, totalPages: 1, totalAssets: assets.length };
  const members = [...grouped.get(location)].sort(sortAssets);
  const totalPages = Math.max(1, Math.ceil(members.length / pageSize));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const visible = members.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const visibleIds = new Set(visible.map((asset) => Number(asset.id)));
  const nodes = [{ id: selected.id, type: "location", label: selected.name, count: members.length, x: 90, y: 90 }, ...visible.map((asset, index) => ({ id: `asset:${asset.id}`, type: "asset", label: assetLabel(asset), asset, x: 320 + index % 4 * 215, y: 80 + Math.floor(index / 4) * 112 }))];
  const edges = [];
  visible.forEach((asset) => {
    let hasVisiblePhysical = false;
    for (const [field, type] of [["switchAssetId", "physical"], ["upsAssetId", "power"]]) {
      const parent = Number(asset[field]);
      if (visibleIds.has(parent) && parent !== Number(asset.id)) {
        edges.push({ source: `asset:${parent}`, target: `asset:${asset.id}`, type, port: type === "physical" ? asset.switchPort || "" : "" });
        hasVisiblePhysical = true;
      }
    }
    if (!hasVisiblePhysical) edges.push({ source: selected.id, target: `asset:${asset.id}`, type: "group", port: "" });
  });
  return { nodes, edges, locations, unlinked, physicalCount, page: safePage, totalPages, totalAssets: assets.length, location: selected.name, locationCount: members.length };
}
